import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Wallet, TrendingUp, Activity, DollarSign, BarChart3, ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { walletsApi } from '../lib/api'
import { formatCurrency, formatDate } from '../lib/utils'
import { MobileTable } from '../components/MobileTable'
import { cn } from '../lib/utils'

interface Trade {
  id: string
  market?: { question?: string }
  side: 'YES' | 'NO'
  amount: number
  price: number
  pnl?: number
  executedAt?: string
}

interface WalletData {
  id: string
  label?: string
  address: string
  isWhale?: boolean
  totalPnL?: number
  winRate?: number
  totalTrades?: number
  sharpeRatio?: number
  trades?: Trade[]
}

export function WalletDetail() {
  const { id } = useParams()

  const { data: wallet, isLoading } = useQuery<WalletData>({
    queryKey: ['wallet', id],
    queryFn: () => walletsApi.getById(id!).then(r => r.data),
    enabled: !!id,
  })

  if (isLoading) {
    return <div className="text-center py-12">Loading wallet...</div>
  }

  if (!wallet) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Wallet not found</p>
        <Link to="/wallets" className="text-blue-600 hover:underline mt-4 inline-block">
          Back to Wallets
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Link to="/wallets" className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{wallet.label || 'Unnamed Wallet'}</h1>
          <p className="font-mono text-gray-500">{wallet.address}</p>
        </div>
        {wallet.isWhale && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
            Whale
          </span>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <DollarSign className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Total PnL</p>
              <p className={`text-2xl font-bold ${(wallet.totalPnL || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(wallet.totalPnL || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Win Rate</p>
              <p className="text-2xl font-bold">{((wallet.winRate || 0) * 100).toFixed(1)}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <BarChart3 className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Sharpe Ratio</p>
              <p className="text-2xl font-bold">{(wallet.sharpeRatio || 0).toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <Activity className="w-8 h-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Total Trades</p>
              <p className="text-2xl font-bold">{wallet.trades?.length || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Trades */}
      <MobileTable
        title="Recent Trades"
        data={wallet.trades || []}
        keyExtractor={(trade) => trade.id}
        emptyState={
          <div className="text-center py-8">
            <Wallet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No trades recorded for this wallet</p>
          </div>
        }
        columns={[
          {
            key: 'market',
            title: 'Market',
            render: (trade) => (
              <span className="text-sm text-gray-900 line-clamp-1">
                {trade.market?.question || 'Unknown Market'}
              </span>
            ),
          },
          {
            key: 'side',
            title: 'Side',
            render: (trade) => (
              <span className={cn(
                'inline-flex items-center px-2 py-1 rounded text-xs font-medium',
                trade.side === 'YES' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              )}>
                {trade.side}
              </span>
            ),
          },
          {
            key: 'amount',
            title: 'Size',
            render: (trade) => formatCurrency(trade.amount),
          },
          {
            key: 'price',
            title: 'Price',
            render: (trade) => `${(trade.price * 100).toFixed(1)}¢`,
            mobileHidden: true,
          },
          {
            key: 'pnl',
            title: 'PnL',
            render: (trade) => (
              <span className={cn(
                'text-sm font-medium',
                (trade.pnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'
              )}>
                {trade.pnl ? formatCurrency(trade.pnl) : '-'}
              </span>
            ),
          },
          {
            key: 'executedAt',
            title: 'Date',
            render: (trade) => formatDate(trade.executedAt),
            mobileHidden: true,
          },
        ]}
      />
    </div>
  )
}
